import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import PageController from './common/pageController';

import {
  axiosInstance,
} from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';


interface QuickstartProgramCommunicationProps {
  screenNum: number;
  maxScreenNum: number;
  changeScreen: (num: number) => void;
}

const roundedGradientButton = {
  borderRadius: '100px',
  width: '164px',
  color: 'black',
  fontWeight: 500,
  fontSize: '20px',
  textTransform: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '100px',
    padding: '2px', //ajust the padding to change the width of the border radius
    background: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
  '&:hover': {
    backgroundImage: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    color: 'white',
  },
};

const QuickstartProgramCommunication = ({
  screenNum,
  maxScreenNum,
  changeScreen,
}: QuickstartProgramCommunicationProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [requestStatus, setRequestStatus] = useState({
    isLoading: false,
    success: false,
    error: false,
    text: '',
  });

  const setPage = (pageNum: number) => {
    changeScreen(pageNum);
  };

  const completeQuickstart = async () => {
    setRequestStatus({
      isLoading: true,
      success: false,
      error: false,
      text: '',
    });
    try {
      const response = await axiosInstance().patch('/tenant/quickstart', {
        has_onboarding_done: true,
      });
      const status = response.status;
      if (status === 200) {
        setRequestStatus({
          isLoading: false,
          success: true,
          error: false,
          text: '',
        });
        navigate('/customer/reports');
      }
    } catch (err: unknown) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Tooltip Guide not found on your account',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        text: errorMessage,
      });
    }
  };

  const updateLaunchMessageHandler = async () => {
    completeQuickstart();
  };

  return (
    <Stack
      sx={{
        minHeight: '100vh',
        scrollSnapAlign: 'center',
      }}
    >
      <Stack
        sx={{
          maxHeight: '100vh',
          flexGrow: 1,
          overflowY: 'auto',
          padding: '90px 60px 90px 64px',
        }}
      >
        <Stack paddingRight='0px'>
          <PageController
            page={screenNum}
            setPage={setPage}
            totalPage={maxScreenNum}
          />
        </Stack>
        <Typography
          sx={{
            mt: '30px',
            fontFamily: 'Schibsted Grotesk, sans-serif',
            fontSize: '28px',
            fontWeight: 700,
            color: '#181818',
            lineHeight: '150%',
            maxWidth: 650,
            marginBottom: '32px',
          }}
        >
          <FormattedMessage defaultMessage="Congratulations! You're all set. Visit the program page to customize your design and update your policy." />
        </Typography>
        <Button sx={roundedGradientButton} onClick={updateLaunchMessageHandler}>
          {requestStatus.isLoading ? (
            <CircularProgress size={20} sx={{ marginY: 1 }} />
          ) : (
            intl.formatMessage({ defaultMessage: 'Finish' })
          )}
        </Button>
      </Stack>
    </Stack>
  );
};

export default QuickstartProgramCommunication;
